<template>
  <div>
    <v-layout v-if="application" align-center class="mb-3">
      <v-breadcrumbs :items="breadcrumbItems" class="pa-0 ml-4"></v-breadcrumbs>
      <v-spacer></v-spacer>
      <div>        
        <v-btn
          v-if="application && application.scheduling && application.scheduling.type === 'NoDevices'"
          outlined
          small
          class="primary--text mr-3"
          @click="startApplication"
        >
          <v-icon small class="mr-1">play_arrow</v-icon>
          Start
        </v-btn>

        <v-btn
          v-if="application && application.scheduling && application.scheduling.type === 'Conditional'"
          outlined
          small
          class="primary--text mr-3"
          @click="stopApplicationDialog = true"
        >
          <v-icon small class="mr-1">stop</v-icon>
          Stop
        </v-btn>

        <application-delete :application="application"></application-delete>
        <application-restart :application="application"></application-restart>

        <v-btn
          outlined
          small
          class="primary--text"
          :to="{
            name: 'applicationAllocations',
            params: {
              project: projectId,
              namespace: namespaceId,
              application: applicationId,
            },
          }"
          router
          exact
        >
          <v-icon small class="mr-1">apps</v-icon>
          Status
        </v-btn>
      </div>
    </v-layout>

    <v-layout>
      <page-subtitle
        >View and edit your application configuration here. View full spec in the
        <a
          href="https://docs.synpse.net/synpse-core/applications/app-specification"
          class="ml-1"
          target="_blank"
          rel="noopener"
          >docs</a
        >.
      </page-subtitle>
      <v-spacer></v-spacer>
      <v-tooltip left>
        <template v-slot:activator="{ on: tooltip }">
          <v-btn
            icon
            v-on="tooltip"
            href="https://docs.synpse.net/synpse-core/applications/app-specification"
            target="_blank"
            rel="noopener"
          >
            <v-icon color="grey">help</v-icon>
          </v-btn>
        </template>
        <span>
          Find out more about the application spec format here:
          https://docs.synpse.net/synpse-core/applications/app-specification
        </span>
      </v-tooltip>
    </v-layout>

    <v-layout class="mb-3" wrap>
      <v-flex lg8 md12 sm12 pa-2>
        <v-layout>
          <v-flex md12>
            <v-card flat outlined>
              <v-card-text>
                <codemirror
                  v-model="applicationYaml"
                  :options="codeMirrorOptions"
                ></codemirror>
              </v-card-text>
              <v-card-actions>
                <v-alert v-if="error" dense outlined type="error">
                  {{ error }}
                </v-alert>
                <v-alert v-if="yamlError" dense outlined type="error">
                  {{ yamlError }}
                </v-alert>
                <v-spacer></v-spacer>

                <v-btn
                  text
                  small
                  class="primary--text"
                  :disabled="loading"
                  :loading="loading"
                  @click="updateApplicationSpec()"
                >
                  <v-icon small class="mr-2">save</v-icon>
                  Save
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-flex>
        </v-layout>

        <v-layout v-if="application && application.templateId && template">
          <v-flex md12>
            <v-card flat outlined class="mt-4">
              <v-card-title class="text-subtitle-2">Usage</v-card-title>
              <v-card-text class="mt-2">

                <v-row align="center" dense>
                  <p class="ml-1">
                    {{ usage }}
                  </p>
                </v-row>

                <div v-if="examples.length > 0">
                  <div v-for="(example, i) in examples" :key="`example-${i}`" class="mt-2">            
                    <v-row v-if="example.title" align="center" dense>
                      <span class="ml-1 mb-2 text-subtitle-2 white--text">{{ example.title }}</span>
                    </v-row>

                    <v-row v-if="example.description" align="center" dense>
                      <p class="ml-1">
                        {{ example.description }}
                      </p>
                    </v-row>

                    <v-row v-if="example.code" align="center" dense>
                      <v-col class="" md="12" sm="12" xs="12">
                      <markup class="" language="bash" :code="example.code" :inline="false"></markup>
                      </v-col>
                    </v-row>
                  </div>
                </div>


              </v-card-text>
            </v-card>            
          </v-flex>          
        </v-layout>
      </v-flex>
      <v-flex lg4 md12 sm12 pa-2>
        <v-card v-if="application" flat outlined>
          <v-card-title class="text-subtitle-2">
            Status
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-icon small v-on="tooltip" color="grey" class="ml-2"
                  >help</v-icon
                >
              </template>
              <span>
                Deployment status tracks application rollout across your fleet.
                Incidents are available if application monitor is configured
              </span>
            </v-tooltip>
          </v-card-title>
          <v-card-text v-if="hints.length > 0">
            <application-hints :hints="hints"> </application-hints>
          </v-card-text>
          <v-card-text>
            <!-- <v-list subheader> -->
            <!-- Pending -->

            <v-layout class="mb-3" wrap>
              <v-flex lg5 md4 sm4>
                <v-list v-if="waitingForScheduler">
                  <v-list-item class="mt-1">
                    <v-list-item-avatar>
                      <v-progress-circular                        
                        indeterminate
                        color="primary"
                      ></v-progress-circular>
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title
                        v-text="`Preparing...`"
                      ></v-list-item-title>
                      <v-list-item-subtitle></v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <v-list v-else>
                  <v-list-item class="mt-1">
                    <v-list-item-avatar>
                      <v-progress-circular
                        v-if="application.deploymentStatus.pending > 0"
                        indeterminate
                        color="primary"
                      ></v-progress-circular>
                      <v-progress-circular
                        v-else
                        :value="100"
                        color="secondary"
                      ></v-progress-circular>
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title
                        v-text="
                          `Pending: ${application.deploymentStatus.pending}`
                        "
                      ></v-list-item-title>
                      <v-list-item-subtitle></v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- Running -->
                  <v-list-item>
                    <v-list-item-avatar>
                      <v-progress-circular
                        :value="
                          (application.deploymentStatus.available /
                            application.deploymentStatus.total) *
                          100
                        "
                        :color="
                          application.deploymentStatus.available > 0
                            ? 'green accent-5'
                            : ''
                        "
                      ></v-progress-circular>
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title
                        v-text="
                          `Running: ${application.deploymentStatus.available}/${application.deploymentStatus.total}`
                        "
                      ></v-list-item-title>
                      <v-list-item-subtitle> </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-flex>
              <v-flex lg7 md8 sm8>
                <v-list v-if="application.notifications">
                  <v-list-item v-if="incidents.length === 0">
                    <v-list-item-avatar>
                      <v-icon small color="grey">fiber_manual_record</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>No incidents</v-list-item-title>
                      <v-list-item-subtitle> </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <div v-else v-for="(item, key) in incidents" :key="key">
                    <!-- DOWN -->
                    <v-tooltip top>
                      <template v-slot:activator="{ on: tooltip }">
                        <v-list-item  v-on="tooltip" v-if="item.status == 0">
                          <v-list-item-avatar>
                            <v-icon small color="error"
                              >fiber_manual_record</v-icon
                            >
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>DOWN</v-list-item-title>
                            <v-list-item-subtitle>{{
                              item.createdAt | date
                            }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <span>
                        {{ item.message }}
                      </span>
                    </v-tooltip>
                    <!-- UP -->
                    <v-list-item v-if="item.status == 1">
                      <v-list-item-avatar>
                        <v-icon small color="green accent-3"
                          >fiber_manual_record</v-icon
                        >
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>UP</v-list-item-title>
                        <v-list-item-subtitle>{{
                          item.createdAt | date
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </div>
                </v-list>
              </v-flex>
            </v-layout>
            <!-- </v-list> -->

            <v-layout class="mb-3"  v-if="application.proxy.tunnels && application.proxy.tunnels.length">
              <v-flex lg12 md12 sm12>
                <v-list dense>
                  <v-list-item dense v-for="(tunnel, i) in application.proxy.tunnels" :key="i">                    
                    <a :href="tunnel.url" target="_blank" rel="noopener" class="wrap-text">
                      {{ tunnel.url }}
                    </a>

                    <v-icon small class="ml-2" @click="copyToClipboard(tunnel.url)">mdi-content-copy</v-icon>
                  </v-list-item>
                </v-list>
              </v-flex>
            </v-layout>            
          </v-card-text>
        </v-card>
        
        <v-card class="mt-4" flat outlined>

          <v-card-title class="text-subtitle-2">
            Device details

            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  icon
                  small
                  v-on="tooltip"
                
                >
                  <v-icon color="grey" small>help</v-icon>
                </v-btn>
              </template>
              <span>
                Select device to view logs for
              </span>
            </v-tooltip>           

          </v-card-title>

          <v-row align="center">
            <v-col cols="6">
              <v-subheader class="mb-1">
                Device
              </v-subheader>
            </v-col>

            <v-col cols="6">
              <v-select
                class="mr-2 mt-3"
                v-model="selectedDeviceAllocation"
                :items="applicationAllocations"
                item-text="deviceName"
                item-value="deviceId"
                label="-"
                persistent-hint
                return-object
                single-line
                dense
                outlined
              ></v-select>
            </v-col>
          </v-row>
          
          <v-card-actions v-if="selectedDeviceAllocation" class="ml-1 mb-2">
            <v-list width="100%">


              <v-list-item>
                <v-spacer></v-spacer>                
                <v-btn class="mr-2" x-small outlined                
                  :to="{
                      name: 'deviceDetails',
                      params: {
                        project: application.projectId,
                        device: selectedDeviceAllocation.deviceId,
                      },
                    }"
                    router
                  >
                  Device Details
                </v-btn>
              </v-list-item>

              <template v-for="(container, i) in application.spec.containers">
                <!-- <v-container fluid> -->
                  <v-list-item :key="i" style="padding-left: 1px;">
                  <v-list-item-content>
                    <!-- <v-col > -->
                    <!-- <div :key="i"> -->
                      <container-status
                        :application="application"
                        :device="{
                          name: selectedDeviceAllocation.deviceName,
                          projectId: projectId,
                        }"
                        :containers="application.spec.containers"
                        :applicationStatus="getContainerStatus(selectedDeviceAllocation, container.name)"
                        :refresh="refresh"
                      ></container-status>              
                    

                      <v-spacer></v-spacer>
                    </v-list-item-content>

                      <v-list-item-action>
                        <ApplicationLogsDialog
                          :device="{ id:selectedDeviceAllocation.deviceId }"
                          :application="application"
                          :container="container"
                        >
                        </ApplicationLogsDialog>
                      </v-list-item-action>  
                    <!-- </div> -->
                    <!-- </v-col> -->
                  </v-list-item>
                <!-- </v-container> -->
              </template>
            </v-list>
          </v-card-actions>          

          <v-card-text v-else>
            <p>
              Select device to view logs for this application
            </p>
          </v-card-text>
        
        </v-card>

        <v-card class="mt-4" flat outlined>
          <v-card-title class="text-subtitle-2">
            Namespace Secrets
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  icon
                  small
                  v-on="tooltip"
                  href="https://docs.synpse.net/applications/secrets"
                  target="_blank"
                  rel="noopener"
                >
                  <v-icon color="grey" small>help</v-icon>
                </v-btn>
              </template>
              <span>
                Secrets can be stored separately from your application spec,
                improving security.
              </span>
            </v-tooltip>
            <v-spacer></v-spacer>
            <secret-create></secret-create>
          </v-card-title>
          <v-card-text>
            <p v-if="secrets.length == 0">
              Secrets enable your applications to receive sensitive data (for
              example cloud credentials, admin passwords, etc.) without adding
              them to the app spec directly.
            </p>
            <v-list v-else subheader two-line dense>
              <v-list-item v-for="(item, key) in secrets" :key="key">
                <v-list-item-avatar>
                  <v-btn icon @click="copySecretToClipboard(item)">
                    <v-icon small color="">mdi-content-copy</v-icon>
                  </v-btn>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title
                    v-text="`${item.name}`"
                  ></v-list-item-title>
                  <v-list-item-subtitle>
                    Created {{ item.createdAt | ago }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="stopApplicationDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">Stop Application</span>
        </v-card-title>
        <v-card-text>
          Are you sure you want to stop application
          <strong>{{ application ? application.name : '' }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-btn class="secondary--text" small text @click="stopApplicationDialog = false"
            >Cancel</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn class="primary" small text @click="confirmStopApplication()"
            >Stop</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deviceSelectionDialog" max-width="600">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Select Devices</span>
          <v-switch
            v-model="showOfflineDevices"
            label="Show offline"
            hide-details
            class="ma-0"
          ></v-switch>
        </v-card-title>
        <v-card-text>
          <v-list v-if="filteredDevices.length > 0">
            <v-list-item
              v-for="device in filteredDevices"
              :key="device.id"
              @click="toggleDeviceSelection(device.id)"
            >
              <v-list-item-action>
                <v-checkbox
                  :value="selectedDeviceIds.includes(device.id)"
                  @click.stop="toggleDeviceSelection(device.id)"
                ></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <v-tooltip top>
                    <template v-slot:activator="{ on: tooltip }">
                      <v-icon
                        v-if="device.status == 'online'"
                        small
                        color="green accent-3"
                        class="mr-2"
                        v-on="tooltip"
                      >fiber_manual_record</v-icon>
                      <v-icon
                        v-else-if="device.status == 'offline'"
                        small
                        color="error"
                        class="mr-2"
                        v-on="tooltip"
                      >fiber_manual_record</v-icon>
                    </template>
                    <span>
                      {{ device.status === 'online' ? 'Online' : 'Offline' }}
                      <span v-if="device.lastSeenAt">
                        - Last seen: {{ device.lastSeenAt | date }} ({{ device.lastSeenAt | ago }})
                      </span>
                    </span>
                  </v-tooltip>
                  {{ device.name }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="device.labels && Object.keys(device.labels).length > 0">
                  {{ Object.entries(device.labels).map(([k, v]) => `${k}=${v}`).join(', ') }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <p v-else class="text-center pa-4">
            <span v-if="devices.length === 0">No devices available in this project</span>
            <span v-else>No online devices available</span>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            small
            :disabled="selectedDeviceIds.length === 0"
            @click="confirmDeviceSelection"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style >
.CodeMirror {
  border: 1px solid #eee;
  height: auto;
}
.wrap-text {
  overflow: hidden;
  width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;

}
</style>

<script>
import PageSubtitle from '../PageSubtitle';
import ApplicationDelete from './ApplicationDelete';
import ApplicationRestart from './ApplicationRestart';
import SecretCreate from '../secrets/SecretCreate';
import ApplicationHints from './ApplicationHints';
import ApplicationLogsDialog from './ApplicationLogsDialog.vue';
import ContainerStatus from '../devices/ContainerStatus.vue';
import Markup from '../helpers/Markup';

import YAML from 'yaml';

// Editor
import { codemirror } from 'vue-codemirror';

// require styles
import 'codemirror/lib/codemirror.css';

// language js
import 'codemirror/mode/yaml/yaml.js'

// theme css
import 'codemirror/theme/base16-dark.css';

export default {
  components: {
    PageSubtitle,
    ApplicationDelete,
    ApplicationRestart,
    SecretCreate,
    ApplicationHints,
    ApplicationLogsDialog,
    ContainerStatus,
    codemirror,
    Markup,
  },
  data() {
    return {
      applicationYaml: '',
      yamlError: null,
      selectedDeviceAllocation: null,
      deviceSelectionDialog: false,
      selectedDeviceIds: [],
      showOfflineDevices: false,
      stopApplicationDialog: false
    }
  },

  computed: {
    breadcrumbItems() {
      return [
        {
          text: 'Applications',
          disabled: false,
          to: {
            name: 'applications',
            params: {
              project: this.projectId,
              namespace: this.namespaceId,
            },
          },
        },
        {
          text: this.application ? this.application.name : '',
          disabled: true,
        },
      ]
    },
    projectId() {
      return this.$route.params.project
    },
    namespaceId() {
      return this.$route.params.namespace
    },
    applicationId() {
      return this.$route.params.application
    },
    application() {
      return this.$store.state.application.application
    },
    secrets() {
      return this.$store.state.secret.secrets
    },
    hints() {
      return this.$store.state.application.applicationHints
    },
    incidents() {
      return this.$store.state.application.applicationIncidents
    },
    applicationAllocations() {
      return this.$store.state.application.applicationAllocations
    },
    devices() {
      return this.$store.state.device.devices
    },
    filteredDevices() {
      if (this.showOfflineDevices) {
        return this.devices
      }
      return this.devices.filter(device => device.status === 'online')
    },
    // Selected secret
    secret() {
      return this.$store.state.secret.secret
    },
    loading() {
      return this.$store.state.application.loading
    },
    error() {
      return this.$store.state.application.error
    },
    theme() {
      return 'vs-dark'
    },
    waitingForScheduler() {
      if (!this.application) {
        return false
      }

      if (!this.application.deploymentStatus) {
        return false
      }
      if (this.application.deploymentStatus.pending > 0) {
        return false 
      }
      if (this.application.deploymentStatus.available > 0) {
        return false
      }  
      // If application is created less than 1 minute ago,
      // then we are still waiting for scheduler
      if (this.application.createdAt) {
        let created = new Date(this.application.createdAt)
        let now = new Date()
        let diff = now - created
        if (diff < 60000) {
          return true
        }
      }
      return false
    },
    codeMirrorOptions() {
      let saveApplication = this.updateApplicationSpec

      let options = {
        // codemirror options
        tabSize: 4,
        mode: 'text/x-yaml',
        theme: 'base16-dark',
        lineNumbers: true,
        line: true,
        extraKeys: {
          "Ctrl-S": function () {
            saveApplication()
            return false;
          }
        }
      }
      return options
    },
    template() {
      return this.$store.state.application.template
    },
    usage() {
      if (!this.template) {
        return ''
      }
      if (!this.template.usage) {
        return ''
      }

      return this.template.usage
    },
    examples() {
      if (this.template.examples) {
        return this.template.examples
      }
      return []
    },
  },

  mounted() {
    this.refresh(false)
    this.subscribe()
  },

  beforeDestroy() {
    this.$store.dispatch('UnsubscribeFromSSE')
  },

  beforeUnmount() {
    this.$store.dispatch('UnsubscribeFromSSE')
  },

  methods: {
    copyToClipboard(val) {
      this.$store.dispatch('Notify', `Copied!`)
      this.$clipboard(val)
    },
    async refresh(background = true) {
      let projectQuery = {
        projectId: this.projectId
      }
      this.$store.dispatch('GetProject', projectQuery)

      let q = {
        projectId: this.projectId,
        namespaceId: this.namespaceId,
        applicationId: this.applicationId,
        background: background,
        limit: 2
      }
      this.$store.dispatch('GetApplicationHints', q)

      this.$store.dispatch('ListApplicationIncidents', q)

      this.$store.dispatch('GetApplication', q).then(() => {
        if (background) {
          // If it's a background refresh, don't update the yaml
          // as it might be edited already
          this.refreshAllocations()
          return
        }

        // stripping down a bit
        const basicManifest = {
          name: this.application.name,
          description: this.application.description,
          type: this.application.container,
          scheduling: this.application.scheduling,
          spec: this.application.spec,          
          monitor: this.application.monitor
        }

        if (this.application.proxy?.tunnels?.length > 0) {
          basicManifest.proxy = this.application.proxy
        }        

        this.applicationYaml = YAML.stringify(basicManifest)

        // List application allocations and select
        // the first one
        this.refreshAllocations()
      })

      // Loading secrets
      this.$store.dispatch('ListSecrets', q)
    },
    refreshAllocations() {
      let q = {
        projectId: this.projectId,
        namespaceId: this.namespaceId,
        applicationId: this.applicationId
      }
      // List application allocations and select
      // the first one
      this.$store.dispatch('ListApplicationAllocations', q).then(() => {
        if (this.applicationAllocations.length === 1) {
          this.selectedDeviceAllocation = this.applicationAllocations[0]
        }
        
        this.getTemplate()
      })
    },
    getTemplate() {
      if (this.application.templateId && this.application.templateId != '') {
        let deviceId = ''
        if (this.selectedDeviceAllocation && this.selectedDeviceAllocation.deviceId) {
          deviceId = this.selectedDeviceAllocation.deviceId
        }

        // Check if there are any ports exposed for appUrl

        let appUrl = ''

        if (this.application.proxy?.tunnels?.length > 0) {
          appUrl = this.application.proxy.tunnels[0].url
        }

        this.$store.dispatch('GetApplicationTemplate', {
          id: this.application.templateId,
          deviceId: deviceId,
          appUrl: appUrl,
        })
      }
    },
    subscribe() {
      let token = this.$store.getters.jwt
      let streamUrl = `/api/projects/${this.projectId}/events?_t=${token}&stream=${this.projectId}:applicationStatuses:${this.applicationId}`

      let q = {
        stream: streamUrl,
        callback: this.refresh
      }

      this.$store.dispatch('SubscribeToSSE', q)
    },
    copySecretToClipboard(secret) {
      this.$store.dispatch('Notify', `Copied!`)
      this.$clipboard(secret.name)
    },
    updateApplicationSpec: function () {
      this.yamlError = null
      let parsed = ''
      let application = ''
      try {
        // Convert all tabs to spaces
        this.applicationYaml = this.applicationYaml.replace(/\t/g, '  ')

        parsed = YAML.parse(this.applicationYaml)

        // Making a copy of the application so vuex doesn't complain
        application = JSON.parse(JSON.stringify(this.application))
      }
      catch (err) {
        this.yamlError = err
        return
      }

      application.name = parsed.name
      application.description = parsed.description
      application.type = parsed.type
      application.scheduling = parsed.scheduling
      application.spec = parsed.spec
      application.monitor = parsed.monitor
      application.proxy = parsed.proxy

      this.$store.dispatch('UpdateApplication', application).then(() => {
        if (this.error === null) {
          // All good, refreshing
          this.refresh(false)
          this.$store.dispatch('Notify', `Application updated`)
          return
        }
        // Showing error
      })
    },
    getContainerStatus(status, name) {
      // Get deploymentStatus for the container
      let containerStatus = status.applicationStatuses?.find(
        (container) => container.name === name
      )
      return containerStatus
    },
    async startApplication() {
      if (!this.application || !this.application.scheduling) {
        return
      }

      const hasDevices = this.application.scheduling.devices && this.application.scheduling.devices.length > 0

      if (!hasDevices) {
        await this.fetchDevices()
        this.selectedDeviceIds = []
        this.showOfflineDevices = false
        this.deviceSelectionDialog = true
      } else {
        await this.updateSchedulingType('Conditional')
      }
    },
    async confirmStopApplication() {
      this.stopApplicationDialog = false
      await this.updateSchedulingType('NoDevices')
    },
    async fetchDevices() {
      let q = {
        projectId: this.projectId,
        query: '',
        pageSize: 1000,
        pageToken: '',
      }
      await this.$store.dispatch('GetDevices', q)
    },
    toggleDeviceSelection(deviceId) {
      const index = this.selectedDeviceIds.indexOf(deviceId)
      if (index > -1) {
        this.selectedDeviceIds.splice(index, 1)
      } else {
        this.selectedDeviceIds.push(deviceId)
      }
    },
    async confirmDeviceSelection() {
      if (this.selectedDeviceIds.length === 0) {
        return
      }

      this.deviceSelectionDialog = false

      const application = JSON.parse(JSON.stringify(this.application))
      application.scheduling = {
        ...application.scheduling,
        type: 'Conditional',
        devices: this.selectedDeviceIds
      }

      await this.$store.dispatch('UpdateApplication', application)
      if (this.error === null) {
        this.refresh(false)
        this.$store.dispatch('Notify', `Application started`)
      }
    },
    async updateSchedulingType(newType) {
      const application = JSON.parse(JSON.stringify(this.application))
      
      if (newType === 'NoDevices') {
        application.scheduling = {
          ...application.scheduling,
          type: newType,          
        }
      } else {
        application.scheduling = {
          ...application.scheduling,
          type: newType
        }
      }

      await this.$store.dispatch('UpdateApplication', application)
      if (this.error === null) {
        this.refresh(false)
        const message = newType === 'Conditional' ? 'Application started' : 'Application stopped'
        this.$store.dispatch('Notify', message)
      }
    }
  }
}
</script>
